import {
  Component,
  effect,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { TravelBoardData } from 'src/app/shared/model/travel-board-data';
import { TravelBoardService } from '../../services/travel-board.service';
import { SidebarService } from 'src/app/shared/components/sidebar/sidebar.service';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LucideAngularModule } from 'lucide-angular';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-chart-filter',
  imports: [SelectButtonModule, LucideAngularModule, FormsModule],
  templateUrl: './chart-filter.component.html',
  styleUrl: './chart-filter.component.scss',
})
export class ChartFilterComponent implements OnInit {
  @Output() sortedTripsEmitter = new EventEmitter<TravelBoardData[]>();
  private travelBoardService = inject(TravelBoardService);
  private sidebarService = inject(SidebarService);
  showPage: boolean = true;
  private translocoService = inject(TranslocoService);
  translatedOptions: { label: string; icon: string; value: () => void }[] = [];
  trips: TravelBoardData[] = [];

  isSidebarCollapsed = this.sidebarService.isCollapsedSignal;

  filters = [
    {
      label: 'travelBoards.emissionsAsc',
      icon: 'trending-up',
      value: () => this.sortByEmissions(true),
    },
    {
      label: 'travelBoards.emissionsDesc',
      icon: 'trending-down',
      value: () => this.sortByEmissions(false),
    },
    {
      label: 'travelBoards.dateAsc',
      icon: 'calendar-plus',
      value: () => this.sortByDate(true),
    },
    {
      label: 'travelBoards.dateDesc',
      icon: 'calendar-minus',
      value: () => this.sortByDate(false),
    },
  ];
  selectedFilter?: () => void;

  constructor() {
    effect(() => {
      this.trips = this.travelBoardService
        .data()
        .filter((trip) => !trip.isDraft);
    });
  }
  /**
   * FILTER FUNCTIONS
   */

  sortByEmissions = (sortAscending: boolean): void => {
    const sortedTrips = [...this.trips].sort((a, b) => {
      const impactA = a.impactPerTraveller;
      const impactB = b.impactPerTraveller;
      return sortAscending ? impactB - impactA : impactA - impactB;
    });
    this.sortedTripsEmitter.emit(sortedTrips);
  };

  sortByDate(sortAscending: boolean): void {
    const sortedTrips = [...this.trips].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortAscending ? dateB - dateA : dateA - dateB;
    });
    this.sortedTripsEmitter.emit(sortedTrips);
  }

  onFilterChange() {
    const filterIndex = this.getFilterIndex();
    localStorage.setItem('filterIndex', filterIndex.toString());
    this.selectedFilter && this.selectedFilter();
  }

  /**
   * INITIALIZATION FUNCTIONS
   */
  async initializeFilterList() {
    this.translatedOptions = await Promise.all(
      this.filters.map(async (filter) => ({
        label: await firstValueFrom(
          this.translocoService.selectTranslate(filter.label),
        ),
        icon: filter.icon,
        value: filter.value,
      })),
    );
  }

  initializeSelectedFilter() {
    const filterIndex: number =
      Number(localStorage.getItem('filterIndex')) || 0;
    this.selectedFilter = this.filters[filterIndex].value;
    this.selectedFilter();
  }

  /**
   * UTIL FUNCTIONS
   */

  getFilterIndex() {
    let filterIndex = 0;
    this.filters.forEach((filter, index) => {
      if (filter.value == this.selectedFilter) {
        filterIndex = index;
      }
    });
    return filterIndex;
  }

  async ngOnInit() {
    await this.initializeFilterList();
    this.initializeSelectedFilter();
  }
}
